import { AsyncPipe } from '@angular/common';
import { Component, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { BbitI18n, BbitObject, BbitString } from '@bbitgmbh/bbit.utils';
import { BbitWebShopAlgoliaService, BbitWebShopService } from '@bbitgmbh/webshop.sdk';
import { I18nPipe } from '@bbitgmbh/website.sdk';
import { NgIcon } from '@ng-icons/core';
import { BehaviorSubject } from 'rxjs';
import { IBbitAlgoliaFilterItem } from '../filter-bar.component';
import { FilterDropdownOverlayRef } from './filter-dropdown.overlay-ref';

@Component({
  selector: 'app-filter-dropdown',
  templateUrl: './filter-dropdown.component.html',
  styleUrl: './filter-dropdown.component.scss',
  imports: [NgIcon, ReactiveFormsModule, FormsModule, AsyncPipe, I18nPipe],
})
export class FilterDropdownComponent {
  private _overlayRef = inject(FilterDropdownOverlayRef);
  algolia = inject(BbitWebShopAlgoliaService);
  private _webShop = inject(BbitWebShopService);

  public allItems: IBbitAlgoliaFilterItem['values'] = [];
  public filteredItems = new BehaviorSubject<IBbitAlgoliaFilterItem['values']>([]);
  public filterValue = new BehaviorSubject<string>('');
  private _currentSort: 'asc' | 'desc' | 'count' = 'count';
  public facet!: string;
  constructor() {
    const _overlayRef = this._overlayRef;

    this.facet = _overlayRef.item._id;
    this.initialize();
  }

  public async initialize() {
    const currentFilters = BbitObject.clone(this.algolia.getCurrentAttributeFilters());
    if (currentFilters[this.facet]) {
      delete currentFilters[this.facet];
    }
    const filters = this.algolia.prepareAttributeFilter(currentFilters);
    const result = await this.algolia.search({
      facets: ['*'],
      filters,
      facetFilters: this.algolia.getCurrentCategoryFilters(),
      hitsPerPage: 0,
    });
    const idResult = result.facets?.[this.facet] || {};
    const attributeId = this.facet.split('.')[1];
    const values: IBbitAlgoliaFilterItem['values'] = [];

    switch (attributeId) {
      case 'manufacturer': {
        const manufacturers = this._webShop.getManufacturer();
        Object.keys(idResult).forEach((valueKey) => {
          const manufacturer = manufacturers.find((o) => BbitString.ensureBeginningHashtag(o._id) === valueKey);
          const count = idResult?.[valueKey] || 0;
          if (manufacturer && count > 0) {
            values.push({
              _id: valueKey,
              designation: { object: manufacturer.designation },
              count,
            });
          }
        });
        break;
      }
      case 'caliber': {
        const calibers = this._webShop.getCalibers();
        Object.keys(idResult).forEach((valueKey) => {
          const caliber = calibers.find((o) => BbitString.ensureBeginningHashtag(o._id) === valueKey);
          const count = idResult?.[valueKey] || 0;
          if (caliber && count > 0) {
            values.push({
              _id: valueKey,
              designation: { object: caliber.designation },
              count,
            });
          }
        });
        break;
      }
      default: {
        const attributes = this._webShop.getAttributes();
        const attributeValues = this._webShop.getAttributeValues();
        const attribute = attributes.find(
          (a) => BbitString.ensureBeginningHashtag(a._id) === BbitString.ensureBeginningHashtag(attributeId),
        );
        if (attribute) {
          Object.keys(idResult).forEach((valueKey) => {
            const attributeValue = attributeValues.find((a) => BbitString.ensureBeginningHashtag(a._id) === valueKey);
            const count = idResult?.[valueKey] || 0;
            if (attributeValue && count > 0) {
              values.push({
                _id: valueKey,
                designation: { object: attributeValue.designation },
                count,
              });
            }
          });
        }
        break;
      }
    }

    this.allItems = values;
    this.filteredItems.next(values);
    this.sort();
  }

  public filter(value: string) {
    this.filterValue.next(value);
    if (!value) {
      this.filteredItems.next(this.allItems);
    } else {
      this.filteredItems.next(
        this.allItems.filter((item) => BbitI18n.t(item.designation).toLowerCase().includes(value.toLowerCase())),
      );
    }
  }

  public clear() {
    this.algolia.clearAttributeFilter(this.facet);
    this._overlayRef.close();
  }

  public toggleSort() {
    switch (this._currentSort) {
      case 'asc':
        this._currentSort = 'desc';
        break;
      case 'desc':
        this._currentSort = 'count';
        break;
      case 'count':
        this._currentSort = 'asc';
        break;
    }
    this.sort();
  }

  public sort() {
    switch (this._currentSort) {
      case 'asc':
        this._currentSort = 'asc';
        this.filteredItems.next(
          this.filteredItems.value.sort((a, b) => {
            return BbitI18n.t(a.designation).localeCompare(BbitI18n.t(b.designation));
          }),
        );
        break;
      case 'desc':
        this._currentSort = 'desc';
        this.filteredItems.value.sort((a, b) => {
          return BbitI18n.t(b.designation).localeCompare(BbitI18n.t(a.designation));
        });
        break;
      case 'count':
        this._currentSort = 'count';
        this.filteredItems.value.sort((a, b) => {
          return b.count - a.count;
        });
        break;
    }
  }
}
